import Definition from '@client/config/definition';
import { i18n } from '@client/helper/i18n.js';
import unitHelper from '@client/helper/unit';
import { isBikeCategoryByName } from '@client/helper/sport';
import * as d3 from 'd3';
import {
  CHART_TYPE_POLYLINE,
  CHART_TYPE_POINT,
  // CHART_TYPE_HISTOGRAM,
  CHART_TYPE_AREA,
  CHART_TYPE_FILL_AREA
} from '@client/pages/activity-detail/config/chart.types';
import { intensityLapData, topSummary, useActivityDetail } from '@client/pages/activity-detail/hooks/useActivityDetail';
import * as corosSportConfig from '@coros/sport-config';
const { isIndoorClimbOrBoulderingTrainingSportType } = corosSportConfig.sport.climb;

import { FREQUENCY_LIST, LAP_LIST, NULL_TEXT, METRIC_UNITS, getPaceUnit, getSpeedUnit, IMPERIAL_UNITS } from '../common';
import { getClimbConfig } from './climb';

const { temperatureUnitType } = useActivityDetail();
const { t } = i18n;

// 图表可能出现的属性字段
export function getChartAttr({ getFrom, thisSpeedUnit, sportName, sportType, chartType, unitType, otherResult }) {
  // 温度单位temperatureUnit特殊逻辑
  const finalTemperatureUnitType = temperatureUnitType.value;

  let sportSpecialConfig = {};
  if (isIndoorClimbOrBoulderingTrainingSportType(sportType.value) || [105, 106].includes(sportType.value)) {
    sportSpecialConfig = getClimbConfig({ getFrom, chartType, unitType, sportType: sportType.value });
  }
  const isBikeSport = isBikeCategoryByName(sportName);
  const chartAttr = {
    ...sportSpecialConfig,
    // 配速
    Pace: {
      text: t('H5003'),
      unit: getPaceUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'speed',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'speed',
          [LAP_LIST]: 'avgPace'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(0, 255, 68, 1)',
      subCharts: ['Pace_Intensity'],
      tickText: (value, d) => {
        if (!value) {
          return NULL_TEXT;
        }
        return unitHelper.getPaceValue(value, sportType.value, 2, unitType);
      },
      defined(d, i, chart, attr) {
        // 控制是否绘制该点（return false不绘制）
        return ![undefined, null, '', 0].includes(d[chart.xAxisKey]);
      },
      y(d, i, chart, attr) {
        const chartName = attr.chartName;
        const valueKey = attr.valueKey;
        if ([undefined, '', 0].includes(d[valueKey]) || isNaN(d[valueKey])) {
          return chart.contentHeight;
        }
        return chart.contentHeight - chart.yScales[chartName](chart.valueCorrect(attr, d[valueKey]));
      }
    },
    // 速度
    Speed: {
      text: t('H5005'),
      unit: getSpeedUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'speed',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'speed',
          [LAP_LIST]: 'avgSpeedV2'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(0, 255, 68, 1)',
      subCharts: ['Speed_Intensity'],
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        const fixed = sportName === 'Speedsurfing' ? 2 : 1;
        return unitHelper.getSpeedValueWithSpeedType(thisSpeedUnit, value, fixed, unitType);
      }
    },
    // 功率
    Power: {
      text: t('H5033'),
      unit: 'w',
      graphObjKey: 'power',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'power',
          [LAP_LIST]: 'avgPower'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(255, 68, 0, 1)',
      subCharts: ['Power_Intensity'],
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      }
    },
    // 跑步效率
    Run_Efficiency: {
      text: t('H5035'),
      unit: '',
      graphObjKey: 'runningEfficiency',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'runningEfficiency',
          [LAP_LIST]: 'runEfficiency'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(0, 255, 238, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return Math.floor(value / 10);
      }
    },
    // 踏地时间
    Ground_Time: {
      text: t('H5009'),
      unit: 'ms',
      graphObjKey: 'groundTime',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'groundTime',
          [LAP_LIST]: 'groundTime'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: 'rgba(255, 204, 0, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      },
      fill(d, i) {
        return 'rgba(255, 204, 0, 1)';
      }
    },
    // 左右平衡
    L_R_Balance: {
      text: t('H5011'),
      unit: '',
      graphObjKey: 'leftGroundBalance',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'leftGroundBalance',
          [LAP_LIST]: 'groundBalance'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: '#AADB37',
      min: isBikeSport ? 0 : 40,
      max: isBikeSport ? 100 : 60,
      ticks: isBikeSport ? [0, 50, 100] : [40, 50, 60],
      tickText: (value, d) => {
        if ([undefined, ''].includes(value) || value >= 100 || value <= 0) {
          return NULL_TEXT;
        }
        const intValue = Math.round(value);
        if (intValue > 50) {
          return `${intValue}%L`;
        }
        if (intValue === 50) {
          return `${intValue}%`;
        }
        if (intValue < 50) {
          return `${100 - intValue}%R`;
        }
      },
      filter(d, i) {
        // 控制是否绘制该点（return false不绘制）
        const valueKey = chartAttr.L_R_Balance.valueKey;
        const value = d[valueKey];
        if (isNaN(value) || [undefined, '', 0].includes(value)) return false;

        if (value >= 100 || value <= 0) return false;
        return true;
      },
      fill(d, i) {
        return '#AADB37';
      }
    },
    // 步频
    Step_Cadence: {
      text: t('H5007'),
      unit: '',
      graphObjKey: 'cadence',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'cadence',
          [LAP_LIST]: 'avgCadence'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: 'rgba(255, 153, 0, 1)',
      subCharts: ['Cadence_Intensity'],
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      },
      fill(d, i) {
        return 'rgba(255, 153, 0, 1)';
      }
    },
    // 踏频
    Cadence: {
      text: t('H5037'),
      unit: '',
      graphObjKey: 'cadence',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'cadence',
          [LAP_LIST]: 'avgCadence'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: 'rgba(255, 153, 0, 1)',
      subCharts: ['Cadence_Intensity'],
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      }
    },
    // 划频
    Swim_StrokeRate: {
      text: t('H5021'),
      unit: 'strokes/min',
      graphObjKey: 'cadence',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'cadence',
          [LAP_LIST]: 'avgStrokeRateLen'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: '#F8C032',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      },
      fill(d, i) {
        return '#F8C032';
      }
    },
    // 桨频
    Row_Stroke_Rate: {
      text: t('H5039'),
      unit: '',
      graphObjKey: 'cadence',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'cadence',
          [LAP_LIST]: 'avgCadence'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: '#FFCD4C',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      }
    },
    // 垂直步幅比
    Stride_Ratio: {
      text: t('H5017'),
      unit: '%',
      graphObjKey: 'verticalStrideRatio',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'verticalStrideRatio',
          [LAP_LIST]: 'strideRatio'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: 'rgba(30, 186, 30, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return parseFloat((value / 10).toFixed(1));
      },
      fill(d, i) {
        return 'rgba(30, 186, 30, 1)';
      }
    },
    // 垂直振幅
    Stride_Height: {
      text: t('H5013'),
      unit: {
        [METRIC_UNITS]: 'cm',
        [IMPERIAL_UNITS]: 'inch'
      }[unitType],
      graphObjKey: 'verticalVibration',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'verticalVibration',
          [LAP_LIST]: 'strideHeight'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: 'rgba(0, 213, 255, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return {
          [METRIC_UNITS]: () => {
            return (value / 10).toFixed(1);
          },
          [IMPERIAL_UNITS]: () => {
            return unitHelper.distanceCM2inch(value / 10, 1, unitType);
          }
        }[unitType]();
      },
      fill(d, i) {
        return 'rgba(0, 213, 255, 1)';
      }
    },
    // 步幅
    Stride_Length: {
      text: t('H5015'),
      unit: {
        [METRIC_UNITS]: 'cm',
        [IMPERIAL_UNITS]: 'ft'
      }[unitType],
      graphObjKey: 'cadenceLength',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'cadenceLength',
          [LAP_LIST]: 'avgStrideLength'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: 'rgba(136, 102, 255, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return {
          [METRIC_UNITS]: () => {
            return value;
          },
          [IMPERIAL_UNITS]: () => {
            return unitHelper.distanceCM2FT(value, 2, unitType);
          }
        }[unitType]();
      }
    },
    // 划距
    Stroke_Length: {
      text: t('H5041'),
      unit: {
        [METRIC_UNITS]: 'm',
        [IMPERIAL_UNITS]: 'ft'
      }[unitType],
      graphObjKey: 'cadenceLength',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'cadenceLength',
          [LAP_LIST]: 'avgStrideLength'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POINT,
      color: '#9f70f6',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return {
          [METRIC_UNITS]: () => {
            return (value / 100).toFixed(2);
          },
          [IMPERIAL_UNITS]: () => {
            return unitHelper.distanceM2ft(value / 100, 2, unitType);
          }
        }[unitType]();
      }
    },
    // 姿势功率
    Form_Power: {
      text: t('H5023'),
      unit: 'w',
      graphObjKey: 'formPower',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'formPower',
          [LAP_LIST]: 'formPower'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(255, 204, 0, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      },
      useIt() {
        console.log(`useIt otherResult?.showFormPower`, otherResult?.showFormPower);
        return otherResult?.showFormPower;
      }
    },
    // 下肢刚度
    Leg_Stiffness: {
      text: t('H5025'),
      unit: {
        [METRIC_UNITS]: 'kN/m',
        [IMPERIAL_UNITS]: 'kN/ft'
      }[unitType],
      graphObjKey: 'legStiffness',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'legStiffness',
          [LAP_LIST]: 'legStiffness'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: '#CC29FF',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return {
          [METRIC_UNITS]: () => {
            return parseFloat((value / 10).toFixed(1));
          },
          [IMPERIAL_UNITS]: () => {
            // 因为是被除数，转换函数要颠倒过来
            // 即被除数要除以 0.3048，则整体要乘以 0.3048
            return Number((value / 10) * 0.3048).toFixed(1);
          }
        }[unitType]();
      },
      useIt({ graphObj }) {
        // 下肢刚度的逻辑为无postureTest的时候都显示，有postureTest的时候runposType !== 0（跑姿特点, 0-无有效数据, 1-地面型跑者, 2-空中型跑者） 才显示
        if (!topSummary.value.postureTest) return graphObj['legStiffness'];

        // 跑姿特点, 0-无有效数据, 1-地面型跑者, 2-空中型跑者
        return topSummary.value.postureTest.runposType !== 0;
      }
    },
    // 踏地角度
    Landing_Angle: {
      text: t('H9043'),
      unit: '°',
      graphObjKey: 'strikeAngle',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'strikeAngle',
          [LAP_LIST]: 'strikeAngle'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: '#4D97FF',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return parseFloat((value / 10).toFixed(1));
      },
      useIt({ graphObj }) {
        if (!topSummary.value.postureTest) return graphObj['strikeAngle'];

        // 跑姿特点, 0-无有效数据, 1-地面型跑者, 2-空中型跑者
        return topSummary.value.postureTest.runposType !== 0;
      }
    },
    // 地反力峰值
    Vertical_Ground_Reaction_Force: {
      text: t('H9042'),
      unit: 'kN',
      graphObjKey: 'maxForce',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'maxForce',
          [LAP_LIST]: 'maxForce'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: '#FF2638',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return parseFloat((value / 10000).toFixed(2));
      },
      useIt({ graphObj }) {
        if (!topSummary.value.postureTest) return graphObj['maxForce'];

        // 跑姿特点, 0-无有效数据, 1-地面型跑者, 2-空中型跑者
        return topSummary.value.postureTest.runposType !== 0;
      }
    },
    Impact_Loading_Rate: {
      text: t('H9041'),
      unit: 'bw/s',
      graphObjKey: 'impactLoadingRate',
      valueKey: 'impactLoadingRate',
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgb(255, 7, 151)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }

        return value.toFixed(0);
      }
    },
    // 坡度
    Slope: {
      text: t('H6009'),
      unit: '',
      graphObjKey: 'slope',
      valueKey: 'slope',
      chartType: CHART_TYPE_FILL_AREA,
      color: 'rgb(125, 218, 234)',
      areaColor: [
        {
          offset: '0%',
          color: '#00d2e6'
        },
        {
          offset: '100%',
          color: '#006771'
        }
      ],
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }

        return `${value.toFixed(0)}%`;
      }
    },
    // 风阻系数
    Air_Power: {
      text: t('H5027'),
      unit: '%',
      graphObjKey: 'airPower',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'airPower',
          [LAP_LIST]: 'airPower'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(189, 192, 198, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value || 0;
      }
    },
    // 划次
    Strokes: {
      text: t('H5019'),
      unit: '',
      graphObjKey: 'swimStorkes',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'swimStorkes',
          [LAP_LIST]: 'strokes'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(0, 255, 238, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      }
    },
    // SWOLF
    SWOLF: {
      text: t('H1029'),
      unit: '',
      graphObjKey: 'swimSwolf',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'swimSwolf',
          [LAP_LIST]: 'avgSwolf'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(136, 102, 255, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      }
    },
    // 海拔
    Elevation: {
      text: t('H5029'),
      unit: {
        [METRIC_UNITS]: 'm',
        [IMPERIAL_UNITS]: 'ft'
      }[unitType],
      graphObjKey: 'altitude',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'altitude',
          [LAP_LIST]: 'altitude'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_AREA,
      color: 'rgba(102, 204, 255, 1)',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        const val = Math.round(unitHelper.distanceM2ft(value, 2, unitType) * 10) / 10;
        return val;
      }
    },
    // 心率
    Heart_Rate: {
      text: t('H5031'),
      unit: 'bpm',
      graphObjKey: 'heart',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'heart',
          [LAP_LIST]: 'avgHr'
        }[getFrom];
      })(),
      chartType: CHART_TYPE_POLYLINE,
      color: 'rgba(255, 0, 85, 1)',
      subCharts: ['Heart_Rate_Intensity'],
      tickText: (value, d) => {
        if (!value) {
          return NULL_TEXT;
        }
        return value;
      }
    },
    // 体温
    Body_Temperature: {
      text: t('H9023'),
      chartType: CHART_TYPE_AREA,
      color: `rgba(255, 193, 7, 1)`,
      areaColor: [
        {
          offset: '0%',
          color: `rgba(255, 193, 7, 0.3)`
        },
        {
          offset: '100%',
          color: `rgba(255, 193, 7, 0.05)`
        }
      ],
      unit: corosSportConfig.basic.temperature.getUnit(finalTemperatureUnitType),
      graphObjKey: 'bodyTemperature',
      valueKey: 'bodyTemperature',
      tickText: (value, d) => {
        if (!value) {
          return NULL_TEXT;
        }

        const isMeric = finalTemperatureUnitType === METRIC_UNITS;
        const val = value / 100;
        value = isMeric ? val.toFixed(2) : corosSportConfig.basic.temperature.convertValueC2F(val, 2);

        return value;
      }
    },
    // TEMPC温度
    TEMPC: {
      text: t('H9091'),
      chartType: CHART_TYPE_AREA,
      color: '#CC5729',
      areaColor: [
        {
          offset: '0%',
          color: `rgb(204, 87, 41, 0.9)`
        },
        {
          offset: '100%',
          color: `rgb(119, 30, 5, 0.1)`
        }
      ],
      unit: corosSportConfig.basic.temperature.getUnit(finalTemperatureUnitType),
      graphObjKey: 'tempc',
      valueKey: 'tempc',
      tickText: (value, d) => {
        if (!value) {
          return NULL_TEXT;
        }

        const isMeric = finalTemperatureUnitType === METRIC_UNITS;
        const val = value / 100;
        value = isMeric ? val : corosSportConfig.basic.temperature.convertValueC2F(val, 0);

        return value;
      }
    },
    // 水温
    Water_Temperature: {
      text: t('H9022'),
      chartType: CHART_TYPE_FILL_AREA,
      color: `rgb(76, 187, 242)`,
      areaColor: [
        {
          offset: '0%',
          color: `rgb(76, 187, 242)`
        },
        {
          offset: '100%',
          color: `rgb(6, 69, 180)`
        }
      ],
      unit: corosSportConfig.basic.temperature.getUnit(finalTemperatureUnitType),
      graphObjKey: 'waterTemperature',
      valueKey: 'waterTemperature',
      tickText: (value, d) => {
        if (!value) {
          return NULL_TEXT;
        }
        const isMeric = finalTemperatureUnitType === METRIC_UNITS;
        const val = value / 100;
        value = isMeric ? val : corosSportConfig.basic.temperature.convertValueC2F(val, 0);

        return value;
      }
    },
    // 等价配速
    Adjusted_Pace: {
      text: t('H9024'),
      unit: getPaceUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'adjustedPace',
      valueKey: 'adjustedPace',
      chartType: CHART_TYPE_POLYLINE,
      color: '#beff00',
      subCharts: ['Adjusted_Pace_Intensity'],
      tickText: (value, d) => {
        if (!value) {
          return NULL_TEXT;
        }
        return unitHelper.getPaceValue(value, sportType.value, 2, unitType);
      },
      defined(d, i, chart, attr) {
        // 控制是否绘制该点（return false不绘制）
        return ![undefined, null, '', 0].includes(d[chart.xAxisKey]);
      },
      y(d, i, chart, attr) {
        const chartName = attr.chartName;
        const valueKey = attr.valueKey;
        if ([undefined, '', 0].includes(d[valueKey]) || isNaN(d[valueKey])) {
          return chart.contentHeight;
        }
        return chart.contentHeight - chart.yScales[chartName](chart.valueCorrect(attr, d[valueKey]));
      }
    },
    // 等价速度
    Adjusted_Speed: {
      text: t('H9025'),
      unit: getSpeedUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'adjustedPace',
      valueKey: 'adjustedPace',
      chartType: CHART_TYPE_POLYLINE,
      color: '#beff00',
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return unitHelper.getSpeedValueWithSpeedType(thisSpeedUnit, value, 2, unitType);
      }
    },

    // 强度-心率
    Heart_Rate_Intensity: {
      text: t('S4492'),
      chartType: CHART_TYPE_FILL_AREA,
      curve: d3.curveStepAfter,
      color: 'rgba(255, 0, 85, 0.2)',
      unit: 'bpm',
      graphObjKey: 'heart',
      noYScale: true,
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        let result = false;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'heart' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = true;
              break;
            }
          }
        }
        return result;
      },
      y0(d, i, chart, attr) {
        let result = 0;
        let intensityCustom = null;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            intensityCustom = item.intensityCustom;
            if (
              Definition.intensityTypeName[item.intensityType] === 'heart' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue
            ) {
              result = item.intensityValue;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result === -1) {
          result = maxValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        if (intensityCustom === 6) {
          return chart.yScales[attr.chartName](maxValue);
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      y1(d, i, chart, attr) {
        let result = 0;
        let intensityCustom = null;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            intensityCustom = item.intensityCustom;
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'heart' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValueExtend
            ) {
              result = item.intensityValueExtend;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result === -1) {
          result = maxValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        if (intensityCustom === 6) {
          return chart.contentHeight - chart.yScales[attr.chartName](result);
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      attrTooltipDefined(d, i) {
        return chartAttr.Heart_Rate_Intensity.defined(d, i);
      },
      tickText: (value, d) => {
        if (!d) {
          return NULL_TEXT;
        }
        let result = '';
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              Definition.intensityTypeName[item.intensityType] === 'heart' &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              const startText = item.intensityValue === -1 ? '' : item.intensityValue;

              const endText = item.intensityValueExtend === -1 ? '' : item.intensityValueExtend;

              result = `${startText}-${endText}`;
              if (!startText || !endText) {
                const prefix = item.intensityCustom === 6 ? t('G3024') : t('G3023');
                result = `${prefix} ${startText || endText}`;
              }
              break;
            }
          }
        }

        return result || NULL_TEXT;
      }
    },

    // 强度-配速
    Pace_Intensity: {
      text: t('S4493'),
      chartType: CHART_TYPE_FILL_AREA,
      curve: d3.curveStepAfter,
      color: 'rgba(0, 255, 68, 0.2)',
      unit: getPaceUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'speed',
      noYScale: true,
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        let result = false;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'pace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = true;
              break;
            }
          }
        }
        return result;
      },
      y0(d, i, chart, attr) {
        let result = 0;
        let intensityCustom = null;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            intensityCustom = item.intensityCustom;
            if (
              Definition.intensityTypeName[item.intensityType] === 'pace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue
            ) {
              const multiplier = item.intensityMultiplier || 1;
              result = item.intensityValue / multiplier;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[1];
        const maxValue = chart.yScales[attr.chartName].domain()[0];

        if (result === -1) {
          result = minValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        if ([6, 7].includes(intensityCustom)) {
          if (intensityCustom === 7) {
            //
          }
          if (intensityCustom === 6) {
            return 0;
          }
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);

        return result;
      },
      y1(d, i, chart, attr) {
        let result = 0;
        let intensityCustom = null;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            intensityCustom = item.intensityCustom;
            if (
              Definition.intensityTypeName[item.intensityType] === 'pace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValueExtend
            ) {
              const multiplier = item.intensityMultiplier || 1;
              result = item.intensityValueExtend / multiplier;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[1];
        const maxValue = chart.yScales[attr.chartName].domain()[0];

        if (result === -1) {
          result = minValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        if ([6, 7].includes(intensityCustom)) {
          if (intensityCustom === 7) {
            result = minValue;
            return chart.yScales[attr.chartName](result);
          }
          if (intensityCustom === 6) {
            return chart.contentHeight - chart.yScales[attr.chartName](result);
          }
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      attrTooltipDefined(d, i) {
        return chartAttr.Pace_Intensity.defined(d, i);
      },
      tickText: (value, d) => {
        if (!d) {
          return NULL_TEXT;
        }

        let result = '';
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (
              Definition.intensityTypeName[item.intensityType] === 'pace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              const multiplier = item.intensityMultiplier || 1;
              const startText =
                item.intensityValue === -1 ? '' : unitHelper.getPaceValue(item.intensityValue / multiplier, sportType.value, 2, unitType);
              const endText =
                item.intensityValueExtend === -1
                  ? ''
                  : unitHelper.getPaceValue(item.intensityValueExtend / multiplier, sportType.value, 2, unitType);

              result = `${startText}-${endText}`;
              if (!startText || !endText) {
                const prefix = item.intensityCustom === 7 ? t('G3023') : t('G3024');
                result = `${prefix} ${startText || endText}`;
              }

              break;
            }
          }
        }

        return result || NULL_TEXT;
      }
    },

    // 强度-等价配速
    Adjusted_Pace_Intensity: {
      text: t('S4653'),
      chartType: CHART_TYPE_FILL_AREA,
      curve: d3.curveStepAfter,
      color: 'rgba(190, 255, 0, 0.2)',
      unit: getPaceUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'adjustedPace',
      noYScale: true,
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        let result = false;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'adjustedPace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = true;
              break;
            }
          }
        }
        return result;
      },
      y0(d, i, chart, attr) {
        let result = 0;
        let intensityCustom = null;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            intensityCustom = item.intensityCustom;
            if (
              Definition.intensityTypeName[item.intensityType] === 'adjustedPace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue
            ) {
              const multiplier = item.intensityMultiplier || 1;
              result = item.intensityValue / multiplier;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[1];
        const maxValue = chart.yScales[attr.chartName].domain()[0];

        if (result === -1) {
          result = minValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        if ([6, 7].includes(intensityCustom)) {
          if (intensityCustom === 7) {
            //
          }
          if (intensityCustom === 6) {
            return 0;
          }
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      y1(d, i, chart, attr) {
        let result = 0;
        let intensityCustom = null;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            intensityCustom = item.intensityCustom;
            if (
              Definition.intensityTypeName[item.intensityType] === 'adjustedPace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValueExtend
            ) {
              const multiplier = item.intensityMultiplier || 1;
              result = item.intensityValueExtend / multiplier;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[1];
        const maxValue = chart.yScales[attr.chartName].domain()[0];

        if (result === -1) {
          result = minValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        if ([6, 7].includes(intensityCustom)) {
          if (intensityCustom === 7) {
            result = minValue;
            return chart.yScales[attr.chartName](result);
          }
          if (intensityCustom === 6) {
            return chart.contentHeight - chart.yScales[attr.chartName](result);
          }
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      attrTooltipDefined(d, i) {
        return chartAttr.Adjusted_Pace_Intensity.defined(d, i);
      },
      tickText: (value, d) => {
        if (!d) {
          return NULL_TEXT;
        }

        let result = '';
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (
              Definition.intensityTypeName[item.intensityType] === 'adjustedPace' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              const multiplier = item.intensityMultiplier || 1;
              const startText =
                item.intensityValue === -1 ? '' : unitHelper.getPaceValue(item.intensityValue / multiplier, sportType.value, 2, unitType);
              const endText =
                item.intensityValueExtend === -1
                  ? ''
                  : unitHelper.getPaceValue(item.intensityValueExtend / multiplier, sportType.value, 2, unitType);

              result = `${startText}-${endText}`;
              if (!startText || !endText) {
                const prefix = item.intensityCustom === 7 ? t('G3023') : t('G3024');
                result = `${prefix} ${startText || endText}`;
              }

              break;
            }
          }
        }

        return result || NULL_TEXT;
      }
    },

    // 强度-速度
    Speed_Intensity: {
      text: t('S4495'),
      chartType: CHART_TYPE_FILL_AREA,
      curve: d3.curveStepAfter,
      color: 'rgba(0, 255, 68, 0.2)',
      unit: getSpeedUnit({ unitType, thisSpeedUnit }),
      graphObjKey: 'speed',
      noYScale: true,
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        let result = false;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'speed' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = true;
              break;
            }
          }
        }
        return result;
      },
      y0(d, i, chart, attr) {
        let result = 0;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (
              Definition.intensityTypeName[item.intensityType] === 'speed' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue
            ) {
              result = item.intensityValue;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result === -1) {
          result = maxValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      y1(d, i, chart, attr) {
        let result = 0;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'speed' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValueExtend
            ) {
              result = item.intensityValueExtend;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result === -1) {
          result = maxValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      attrTooltipDefined(d, i) {
        return chartAttr.Speed_Intensity.defined(d, i);
      },
      tickText: (value, d) => {
        if (!d) {
          return NULL_TEXT;
        }

        let result = '';
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'speed' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              const startText =
                item.intensityValue === -1 ? '' : unitHelper.getSpeedValueWithSpeedType(thisSpeedUnit, item.intensityValue, 2, unitType);
              const endText =
                item.intensityValueExtend === -1
                  ? ''
                  : unitHelper.getSpeedValueWithSpeedType(thisSpeedUnit, item.intensityValueExtend, 2, unitType);

              result = `${startText}-${endText}`;
              if (!startText || !endText) {
                result = `${t('G3023')} ${startText || endText}`;
              }
              break;
            }
          }
        }

        return result || NULL_TEXT;
      }
    },

    // 强度-功率
    Power_Intensity: {
      text: t('S4494'),
      chartType: CHART_TYPE_FILL_AREA,
      curve: d3.curveStepAfter,
      color: 'rgba(255, 68, 0, 0.2)',
      unit: 'w',
      graphObjKey: 'power',
      noYScale: true,
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        let result = false;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              ['power', 'ftp'].includes(Definition.intensityTypeName[item.intensityType]) &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = true;
              break;
            }
          }
        }
        return result;
      },
      y0(d, i, chart, attr) {
        let result = 0;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              ['power', 'ftp'].includes(Definition.intensityTypeName[item.intensityType]) &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue
            ) {
              result = item.intensityValue;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result === -1) {
          result = maxValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      y1(d, i, chart, attr) {
        let result = 0;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              ['power', 'ftp'].includes(Definition.intensityTypeName[item.intensityType]) &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValueExtend
            ) {
              result = item.intensityValueExtend;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result === -1) {
          result = maxValue;
        }

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      attrTooltipDefined(d, i) {
        return chartAttr.Power_Intensity.defined(d, i);
      },
      tickText: (value, d) => {
        if (!d) {
          return NULL_TEXT;
        }

        let result = '';
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              ['power', 'ftp'].includes(Definition.intensityTypeName[item.intensityType]) &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              const startText = item.intensityValue === -1 ? '' : item.intensityValue;

              const endText = item.intensityValueExtend === -1 ? '' : item.intensityValueExtend;

              result = `${startText}-${endText}`;
              if (!startText || !endText) {
                result = `${t('G3023')} ${startText || endText}`;
              }
              break;
            }
          }
        }

        return result || NULL_TEXT;
      }
    },

    // 强度-步频、踏频
    Cadence_Intensity: {
      text: (() => {
        if (['Run', 'Indoor_Run', 'Trail_Run', 'Track_Run', 'XC_Ski', 'Ski_Touring', 'Walk'].includes(sportName)) {
          return t('S4497');
        }

        if (
          ['Bike', 'Road_Ebike', 'Gravel_Road_Bike', 'Mountain_Riding', 'Mountain_Ebike', 'Helmet_Bike', 'Indoor_Bike'].includes(sportName)
        ) {
          return t('S4496');
        }
        return '';
      })(),
      chartType: CHART_TYPE_FILL_AREA,
      curve: d3.curveStepAfter,
      color: 'rgba(255, 153, 0, 0.2)',
      unit: '',
      graphObjKey: 'cadence',
      noYScale: true,
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        let result = false;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (
              Definition.intensityTypeName[item.intensityType] === 'cadence' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = true;
              break;
            }
          }
        }
        return result;
      },
      y0(d, i, chart, attr) {
        let result = 0;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            if (
              Definition.intensityTypeName[item.intensityType] === 'cadence' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue
            ) {
              result = item.intensityValue;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      y1(d, i, chart, attr) {
        let result = 0;
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'cadence' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValueExtend
            ) {
              result = item.intensityValueExtend;
              break;
            }
          }
        }

        const minValue = chart.yScales[attr.chartName].domain()[0];
        const maxValue = chart.yScales[attr.chartName].domain()[1];

        if (result < minValue) {
          result = minValue;
        }

        if (result > maxValue) {
          result = maxValue;
        }

        result = chart.contentHeight - chart.yScales[attr.chartName](result);
        return result;
      },
      attrTooltipDefined(d, i) {
        return chartAttr.Cadence_Intensity.defined(d, i);
      },
      tickText: (value, d) => {
        if (!d) {
          return NULL_TEXT;
        }

        let result = '';
        if (chartType !== 'index-rect') {
          for (let n = 0, length = intensityLapData.value.length; n < length; n++) {
            const item = intensityLapData.value[n];
            // timestamp
            // startTimestamp
            // endTimestamp
            if (
              Definition.intensityTypeName[item.intensityType] === 'cadence' &&
              item.startTimestamp <= d.timestamp &&
              d.timestamp <= item.endTimestamp &&
              item.intensityValue &&
              item.intensityValueExtend
            ) {
              result = `${item.intensityValue}-${item.intensityValueExtend}`;
              break;
            }
          }
        }

        return result || NULL_TEXT;
      }
    },
    // 连跳
    Continuous_Jump: {
      text: t('H9027'),
      chartType: CHART_TYPE_POLYLINE,
      color: '#00FFEE',
      unit: '',
      graphObjKey: 'jump',
      valueKey: (() => {
        return {
          [FREQUENCY_LIST]: 'jump',
          [LAP_LIST]: 'maxJump'
        }[getFrom];
      })(),
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return value;
      }
      // filter(d, i, attr) {
      //   return !unitHelper.isNoValue(d[attr.valueKey])
      // }
    },
    // 齿轮比
    Gear_Ratio: {
      text: t('H9089'),
      unit: '',
      graphObjKey: 'gearRatio',
      valueKey: 'gearRatio',
      curve: d3.curveLinear,
      chartType: CHART_TYPE_AREA,
      color: '#7B3CD0',
      // areaColor: [
      //   {
      //     offset: '0%',
      //     color: 'rgba(123, 60, 208, 1)'
      //   },
      //   {
      //     offset: '100%',
      //     color: 'rgba(39, 6, 110, 0.5)'
      //   }
      // ],
      defined(d, i) {
        // 控制是否绘制该点（return false不绘制）
        const value = d.gearRatio;
        if (value != undefined) {
          return true;
        }
        return false;
      },
      tickText: (value, d) => {
        if (unitHelper.isNoValue(value)) {
          return NULL_TEXT;
        }
        return (Math.round(value * 10) / 100).toFixed(1);
      }
    }
  };

  return chartAttr;
}
